import { useIntervalFn } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { useIdle } from '@/composable/useIdle';
import { assignArray } from '@/core/lib/array';
import BusinessLogicHelper from '@/core/lib/businessLogicHelper';
import { deepFreeze } from '@/core/lib/utils';
import { formatTranslation } from '@/core/oddsApi/helpers/formatTranslation';
import type { CurrentTournamentsFragment } from '@/core/oddsApi/oddsApiType';
import { useCurrentTournamentsQuery } from '@/core/oddsApi/oddsApiType';
import type { ILeague } from '@/interface/ILeague';
import { LeagueSize, SportType } from '@/interface/enum';
import { useOddsApiStore } from '@/store/oddsApiStore';

const formatLeague = (league: CurrentTournamentsFragment): ILeague => ({
    id: league.id,
    name: league.tournamentName.map(x => formatTranslation(x)),
    info: league.tournamentInfo.map(x => formatTranslation(x)),
    size: LeagueSize.fromOddsApi(league.tournamentSize),
    region: {
        id: league.region?.id ?? 0,
        name: league.region?.regionName.map(x => formatTranslation(x)) ?? [],
    },
});

export function useCurrentLeagues(sportType: Ref<SportType>) {
    const { customerSettings, additionalSettings } = storeToRefs(useOddsApiStore());
    const variables = computed(() => ({
        query: {
            sport: SportType.toOddsApi(sportType.value),
        },
    }));
    const { loaded, loading, onResult, refetch } = useCurrentTournamentsQuery(
        variables,
        computed(() => ({
            enabled: true,
        })),
    );

    useIntervalFn(refetch, 60 * 1000);

    // Automatically recover from idle state with a force refresh
    // This is to prevent the user from losing data when they are idle
    const { onActive } = useIdle(60 * 1000);
    onActive(() => {
        // eslint-disable-next-line no-console
        console.log('useCurrentLeagues: Recovering from idle state...');
        refetch();
    });

    const leagues = ref<ILeague[]>([]);

    onResult((result) => {
        if (result) {
            const formatted = result
                .filter((item) => {
                    const league = {
                        id: item.id,
                        isSimulation: item.isSimulation,
                        sport: item.sport,
                    };
                    return BusinessLogicHelper.isLeagueAllowed(customerSettings.value, league, additionalSettings.value);
                }).map(item => deepFreeze(formatLeague(item)));

            assignArray(leagues.value, formatted, item => item.id);
        }
    });

    return {
        loaded,
        loading,
        refetch,
        leagues,
    };
}
